import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';

const ResetPassword = () => {
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [verifyingToken, setVerifyingToken] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [tokenValid, setTokenValid] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError('Invalid reset link. Please request a new password reset.');
                setVerifyingToken(false);
                return;
            }

            try {
                const response = await authAPI.verifyResetToken(token);
                if (response.valid) {
                    setTokenValid(true);
                    setUserEmail(response.email);
                } else {
                    setError('This reset link has expired. Please request a new password reset.');
                }
            } catch (err) {
                setError(err.response?.data?.message || 'This reset link is invalid or has expired.');
            } finally {
                setVerifyingToken(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const validatePassword = (password) => {
        if (password.length < 6) {
            return 'Password must be at least 6 characters long';
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        // Validation
        const passwordError = validatePassword(formData.newPassword);
        if (passwordError) {
            setError(passwordError);
            setLoading(false);
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            await authAPI.resetPassword(token, formData.newPassword);

            setMessage('Password has been reset successfully! You can now log in with your new password.');
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);

        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (verifyingToken) {
        return (
            <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
                {/* Left Side: Blue BG with White Loading Card */}
                <div className="w-full lg:w-1/2 min-w-[1024p] flex-none bg-blue-600 flex justify-end py-8">
                    <div className="max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]">
                        <div className="px-4 sm:px-6 lg:px-8 text-center">
                            {/* Loading Spinner */}
                            <div className="flex justify-center mb-6">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
                            </div>

                            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Verifying reset link...</h2>
                            <p className="text-gray-600 text-sm mb-6">Please wait while we verify your password reset link</p>
                        </div>
                    </div>
                </div>

                {/* Right Side: Info Card */}
                <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
                    <div className="bg-blue-600 text-white shadow-xl p-9 max-w-xl w-full min-h-[600px]">
                        <div className="flex flex-col justify-center h-full">
                            {/* Profile Image */}
                            <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
                                <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
                            </div>

                            {/* Heading */}
                            <h2 className="text-2xl font-bold text-center mb-3">Secure Password Reset</h2>
                            <p className="text-base text-white/90 text-center mb-8">Your account security is our priority</p>

                            {/* Feature List */}
                            <div className="space-y-6 text-left w-full max-w-sm mx-auto">
                                {/* Feature 1 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Encrypted Security</p>
                                        <p className="text-sm text-white/80">Your data is protected with bank-level encryption</p>
                                    </div>
                                </div>

                                {/* Feature 2 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Verified Process</p>
                                        <p className="text-sm text-white/80">Secure token-based password reset</p>
                                    </div>
                                </div>

                                {/* Feature 3 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Instant Access</p>
                                        <p className="text-sm text-white/80">Get back to your care immediately</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!tokenValid) {
        return (
            <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
                {/* Left Side: Blue BG with White Error Card */}
                <div className="w-full lg:w-1/2 min-w-[1024p] flex-none bg-blue-600 flex justify-end py-8">
                    <div className="max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]">
                        <div className="px-4 sm:px-6 lg:px-8 text-center">
                            {/* Error Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                            </div>

                            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Invalid Reset Link</h2>
                            <p className="text-gray-600 text-sm mb-6">{error}</p>

                            <div className="space-y-4">
                                <Link
                                    to="/forgot-password"
                                    className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                                >
                                    Request New Reset Link
                                </Link>
                                
                                <Link
                                    to="/login"
                                    className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                                >
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Info Card */}
                <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
                    <div className="bg-blue-600 text-white shadow-xl p-9 max-w-xl w-full min-h-[600px]">
                        <div className="flex flex-col justify-center h-full">
                            {/* Profile Image */}
                            <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
                                <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
                            </div>

                            {/* Heading */}
                            <h2 className="text-2xl font-bold text-center mb-3">Need Help?</h2>
                            <p className="text-base text-white/90 text-center mb-8">We're here to help you recover your account</p>

                            {/* Feature List */}
                            <div className="space-y-6 text-left w-full max-w-sm mx-auto">
                                {/* Feature 1 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">24/7 Support</p>
                                        <p className="text-sm text-white/80">Our team is always ready to help</p>
                                    </div>
                                </div>

                                {/* Feature 2 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Quick Recovery</p>
                                        <p className="text-sm text-white/80">Get a new reset link in minutes</p>
                                    </div>
                                </div>

                                {/* Feature 3 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.414-4.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Secure Process</p>
                                        <p className="text-sm text-white/80">Your account safety is guaranteed</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (message) {
        return (
            <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
                {/* Left Side: Blue BG with White Success Card */}
                <div className="w-full lg:w-1/2 min-w-[1024p] flex-none bg-blue-600 flex justify-end py-8">
                    <div className="max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]">
                        <div className="px-4 sm:px-6 lg:px-8 text-center">
                            {/* Success Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>

                            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Password Reset Successful</h2>
                            <p className="text-gray-600 text-sm mb-6">{message}</p>

                            <div className="space-y-4">
                                <p className="text-sm text-gray-500">You will be redirected to login in a few seconds...</p>
                                
                                <Link
                                    to="/login"
                                    className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                                >
                                    Go to Login Now
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Info Card */}
                <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
                    <div className="bg-blue-600 text-white shadow-xl p-9 max-w-xl w-full min-h-[600px]">
                        <div className="flex flex-col justify-center h-full">
                            {/* Profile Image */}
                            <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
                                <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
                            </div>

                            {/* Heading */}
                            <h2 className="text-2xl font-bold text-center mb-3">Welcome Back!</h2>
                            <p className="text-base text-white/90 text-center mb-8">Your account is ready for your care journey</p>

                            {/* Feature List */}
                            <div className="space-y-6 text-left w-full max-w-sm mx-auto">
                                {/* Feature 1 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <img src="/images/SVG1.png" alt="Brain icon" className="w-5 h-5 object-contain" />
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Continue Your Care</p>
                                        <p className="text-sm text-white/80">Access your personalized treatment plan</p>
                                    </div>
                                </div>

                                {/* Feature 2 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <img src="/images/SVG 2.png" alt="Brain icon" className="w-5 h-5 object-contain" />
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Secure Account</p>
                                        <p className="text-sm text-white/80">Your new password is safely encrypted</p>
                                    </div>
                                </div>

                                {/* Feature 3 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <img src="/images/SVG 3.png" alt="Brain icon" className="w-5 h-5 object-contain" />
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Professional Support</p>
                                        <p className="text-sm text-white/80">Connect with healthcare experts</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
            {/* Left Side: Blue BG with White Reset Password Card */}
            <div className="w-full lg:w-1/2 min-w-[1024p] flex-none bg-blue-600 flex justify-end py-8">
                <form
                    onSubmit={handleSubmit}
                    className="max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]"
                >
                    <div className="px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center">Reset your password</h2>
                        <p className="text-gray-600 text-sm mb-6 text-center">
                            For account: <span className="font-medium text-gray-900">{userEmail}</span>
                        </p>

                        {/* Error Display */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* New Password Input */}
                        <div className="mt-4">
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="newPassword"
                                    id="newPassword"
                                    placeholder="Enter new password (min. 6 characters)"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password Input */}
                        <div className="mt-4">
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    id="confirmPassword"
                                    placeholder="Confirm your new password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="w-full pt-6">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Resetting Password...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </div>

                        <p className="text-center text-sm mt-4 text-gray-600">
                            Remember your password?{' '}
                            <Link to="/login" className="text-blue-600 hover:underline font-medium">Back to Login</Link>
                        </p>
                    </div>
                </form>
            </div>

            {/* Right Side: Info Card */}
            <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
                <div className="bg-blue-600 text-white shadow-xl p-9 max-w-xl w-full min-h-[600px]">
                    <div className="flex flex-col justify-center h-full">
                        {/* Profile Image */}
                        <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
                            <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
                        </div>

                        {/* Heading */}
                        <h2 className="text-2xl font-bold text-center mb-3">Secure Password Reset</h2>
                        <p className="text-base text-white/90 text-center mb-8">Create a strong new password for your account</p>

                        {/* Feature List */}
                        <div className="space-y-6 text-left w-full max-w-sm mx-auto">
                            {/* Feature 1 */}
                            <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-semibold">Strong Encryption</p>
                                    <p className="text-sm text-white/80">Your password is protected with advanced security</p>
                                </div>
                            </div>

                            {/* Feature 2 */}
                            <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-semibold">Verified Process</p>
                                    <p className="text-sm text-white/80">Secure and authenticated password change</p>
                                </div>
                            </div>

                            {/* Feature 3 */}
                            <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-semibold">Instant Access</p>
                                    <p className="text-sm text-white/80">Immediate login with your new password</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword; 